import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, hoyCaracas } from '../utils/format';
import { pref } from '../utils/moneda';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Costo real de actividades (#102, solo reporte): por cada actividad
// (producto contable de las estructuras de costo), el costo escrito a mano
// frente al que sale de los gastos reales de su categoría ÷ la base del mes
// (kg producidos o unidades vendidas). No cambia ningún costo. La categoría
// de cada actividad se elige acá (contador o más).
// ============================================================

const JERARQUIA = ['consultor', 'operador', 'contador', 'gerente', 'admin'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const nombreMes = (m) => `${MESES[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

function mesesAtras(mes, k) {
  const [a, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1 - k, 1)).toISOString().slice(0, 7);
}

// Promedio ponderado del período: gasto total ÷ base total de los meses con base.
function promedio(a) {
  let gasto = 0;
  let base = 0;
  a.meses.forEach((f) => { if (f.real != null) { gasto += f.gasto; base += f.base; } });
  return base > 0 ? gasto / base : null;
}

function diferencia(real, aMano) {
  if (real == null || !(aMano > 0)) return null;
  return ((real - aMano) / aMano) * 100;
}

function ClaseDif({ valor }) {
  if (valor == null) return <span className="text-slate-300">—</span>;
  const clase = valor > 10 ? 'text-red-600' : valor < -10 ? 'text-green-700' : 'text-slate-600';
  return <span className={clase}>{valor > 0 ? '+' : ''}{formatMonto(valor, 1)} %</span>;
}

function SelectorCategoria({ actividad, categorias, puedeEditar, onCambio }) {
  if (!puedeEditar) {
    return actividad.categoria_nombre || <span className="text-amber-600">Sin categoría</span>;
  }
  const padres = categorias.filter((c) => !c.padre_id);
  return (
    <select
      value={actividad.categoria_id || ''}
      onChange={(e) => onCambio(actividad, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className={`border rounded-md px-2 py-1 text-sm max-w-[13rem] ${actividad.categoria_id ? '' : 'text-amber-700 border-amber-300'}`}
      aria-label={`Categoría de gasto de ${actividad.nombre}`}
    >
      <option value="">Elegir categoría…</option>
      {padres.map((p) => (
        <Fragment key={p.id}>
          <option value={p.id}>{p.nombre}</option>
          {categorias.filter((h) => h.padre_id === p.id).map((h) => (
            <option key={h.id} value={h.id}>{'   '}{h.nombre}</option>
          ))}
        </Fragment>
      ))}
    </select>
  );
}

export default function ReporteCostoActividades() {
  const { empresaActiva, empresaActivaNombre, rolActual, esAdminGlobal } = useAuth();
  const rol = esAdminGlobal ? 'admin' : rolActual || 'consultor';
  const puedeEditar = JERARQUIA.indexOf(rol) >= JERARQUIA.indexOf('contador');
  const hoyMes = hoyCaracas().slice(0, 7);
  const [hasta, setHasta] = useState(hoyMes);
  const [desde, setDesde] = useState(() => mesesAtras(hoyMes, 5));
  const [vista, setVista] = useVistaMoneda();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [abiertos, setAbiertos] = useState(() => new Set());

  function cargar() {
    setCargando(true);
    setError('');
    return api.get('/reportes/costo-actividades', { params: { desde, hasta, vista } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo calcular el costo de las actividades'))
      .finally(() => setCargando(false));
  }

  useEffect(() => {
    if (empresaActiva && desde && hasta) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, desde, hasta, vista]);

  async function cambiarCategoria(actividad, categoriaId) {
    try {
      await api.put(`/reportes/costo-actividades/${actividad.id}/categoria`, { categoria_id: categoriaId || null });
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar la categoría');
    }
  }

  function alternar(id) {
    setAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  const fijas = useMemo(() => (datos?.actividades || []).filter((a) => a.modo !== 'porcentaje'), [datos]);
  const porcentuales = useMemo(() => (datos?.actividades || []).filter((a) => a.modo === 'porcentaje'), [datos]);
  const p = pref(vista);
  const unidadBase = datos?.base.tipo === 'kg' ? 'kg' : 'unidad';

  function contenidoExcel() {
    const aMano = (a) => a.meses[a.meses.length - 1]?.a_mano;
    return {
      titulo: 'Costo real de actividades',
      detalle: `${nombreMes(datos.meses[0])} a ${nombreMes(datos.meses[datos.meses.length - 1])} · base: ${datos.base.etiqueta} · montos en ${vista}`,
      hojas: [
        {
          nombre: 'Por unidad',
          columnas: [
            { titulo: 'Actividad' }, { titulo: 'Categoría de gasto' }, { titulo: 'Mes' },
            { titulo: `Gasto ${vista}`, tipo: 'monto' }, { titulo: `Base (${datos.base.etiqueta})`, tipo: 'monto' },
            { titulo: `Costo real ${vista}`, tipo: 'precio' }, { titulo: `Costo a mano ${vista}`, tipo: 'precio' }, { titulo: 'Diferencia %', tipo: 'porcentaje' }
          ],
          filas: fijas.flatMap((a) => [
            ...a.meses.map((f) => [
              a.nombre, a.categoria_nombre || 'Sin categoría', nombreMes(f.mes),
              f.gasto, f.base, f.real, f.a_mano, diferencia(f.real, f.a_mano)
            ]),
            { valores: [a.nombre, 'Promedio del período', '', null, null, promedio(a), aMano(a), diferencia(promedio(a), aMano(a))], negrita: true }
          ])
        },
        ...(porcentuales.length ? [{
          nombre: 'En porcentaje',
          columnas: [
            { titulo: 'Actividad' }, { titulo: 'Categoría de gasto' }, { titulo: 'Mes' },
            { titulo: `Gasto ${vista}`, tipo: 'monto' }, { titulo: `Ventas ${vista}`, tipo: 'monto' },
            { titulo: '% de las ventas', tipo: 'porcentaje' }, { titulo: '% en estructuras' }
          ],
          filas: porcentuales.flatMap((a) => a.meses.map((f) => [
            a.nombre, a.categoria_nombre || 'Sin categoría', nombreMes(f.mes), f.gasto, f.ventas, f.real, textoPct(a.pct_recetas)
          ]))
        }] : [])
      ]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion
        titulo="Costo real de actividades"
        detalle={datos ? `${nombreMes(datos.meses[0])} a ${nombreMes(datos.meses[datos.meses.length - 1])} · base: ${datos.base.etiqueta} · montos en ${vista}` : ''}
      />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Costo real de actividades
            {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
            <BotonAyuda />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Lo que cuesta cada actividad según los gastos reales: gasto de su categoría ÷ {datos?.base.etiqueta || 'base'} del mes. No cambia ningún costo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="month" value={desde} max={hasta} onChange={(e) => e.target.value && setDesde(e.target.value)} className="border rounded-md px-3 py-2 text-sm" aria-label="Desde" />
          <span className="text-sm text-slate-400">a</span>
          <input type="month" value={hasta} min={desde} onChange={(e) => e.target.value && setHasta(e.target.value)} className="border rounded-md px-3 py-2 text-sm" aria-label="Hasta" />
          <SelectorMoneda vista={vista} onChange={setVista} />
          <Exportar excel={contenidoExcel} disabled={!datos || datos.actividades.length === 0} />
        </div>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {cargando && !datos && <p className="text-slate-400 text-sm">Calculando…</p>}

      {datos && datos.actividades.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">
          Esta empresa no tiene actividades. Se crean en Productos con disponibilidad "Contable / administrativo".
        </div>
      )}

      {datos && fijas.length > 0 && (
        <section className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-slate-700 p-4 border-b">
            Actividades por unidad
            <span className="font-normal text-slate-400 text-sm"> · costo por {unidadBase} en {vista}</span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">Actividad</th>
                  <th className="p-3">Categoría de gasto</th>
                  {datos.meses.map((m) => <th key={m} className="p-3 text-right whitespace-nowrap capitalize">{nombreMes(m)}</th>)}
                  <th className="p-3 text-right whitespace-nowrap">Promedio real</th>
                  <th className="p-3 text-right whitespace-nowrap">A mano</th>
                  <th className="p-3 text-right">Diferencia</th>
                </tr>
                <tr className="text-xs text-slate-400 border-t">
                  <td className="px-3 py-1.5" colSpan={2}>{datos.base.etiqueta.charAt(0).toUpperCase() + datos.base.etiqueta.slice(1)}</td>
                  {datos.base_por_mes.map((b) => (
                    <td key={b.mes} className="px-3 py-1.5 text-right tabular-nums">{b.cantidad ? formatMonto(b.cantidad, datos.base.tipo === 'kg' ? 2 : 0) : '—'}</td>
                  ))}
                  <td colSpan={3} />
                </tr>
              </thead>
              <tbody>
                {fijas.map((a) => {
                  const prom = promedio(a);
                  const vigente = a.meses[a.meses.length - 1]?.a_mano;
                  const abierto = abiertos.has(a.id);
                  return (
                    <Fragment key={a.id}>
                      <tr {...filaClickeable(() => alternar(a.id))} className={`border-t hover:bg-slate-50 cursor-pointer ${FOCO_FILA}`}>
                        <td className="p-3 font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            {abierto ? <ChevronDown size={14} className="text-slate-400 print:hidden" /> : <ChevronRight size={14} className="text-slate-400 print:hidden" />}
                            {a.nombre}
                          </span>
                        </td>
                        <td className="p-3">
                          <SelectorCategoria actividad={a} categorias={datos.categorias} puedeEditar={puedeEditar} onCambio={cambiarCategoria} />
                        </td>
                        {a.meses.map((f) => (
                          <td key={f.mes} className="p-3 text-right tabular-nums whitespace-nowrap">
                            {f.real != null ? `${p}${formatMonto(f.real, 4)}` : <span className="text-slate-300">—</span>}
                          </td>
                        ))}
                        <td className="p-3 text-right tabular-nums whitespace-nowrap font-semibold">{prom != null ? `${p}${formatMonto(prom, 4)}` : <span className="text-slate-300">—</span>}</td>
                        <td className="p-3 text-right tabular-nums whitespace-nowrap">{vigente != null ? `${p}${formatMonto(vigente, 4)}` : <span className="text-amber-600">sin costo</span>}</td>
                        <td className="p-3 text-right tabular-nums whitespace-nowrap"><ClaseDif valor={diferencia(prom, vigente)} /></td>
                      </tr>
                      {abierto && (
                        <tr className="bg-slate-50">
                          <td colSpan={datos.meses.length + 5} className="px-3 pb-3 pt-0">
                            <table className="w-full text-xs">
                              <thead className="text-slate-400 text-left">
                                <tr>
                                  <th className="p-2 pl-6">Mes</th>
                                  <th className="p-2 text-right">Gasto</th>
                                  <th className="p-2 text-right">{datos.base.etiqueta.charAt(0).toUpperCase() + datos.base.etiqueta.slice(1)}</th>
                                  <th className="p-2 text-right">Costo real</th>
                                  <th className="p-2 text-right">A mano ese mes</th>
                                  <th className="p-2 text-right">Diferencia</th>
                                </tr>
                              </thead>
                              <tbody>
                                {a.meses.map((f) => (
                                  <tr key={f.mes} className="border-t border-slate-200">
                                    <td className="p-2 pl-6 capitalize">{nombreMes(f.mes)}</td>
                                    <td className="p-2 text-right tabular-nums">
                                      {f.gasto != null ? `${p}${formatMonto(f.gasto)}` : '—'}
                                      {f.sin_tasa > 0 && <span className="ml-1 text-amber-600" title="Egresos en Bs sin tasa del día: no se sumaron">({f.sin_tasa} sin tasa)</span>}
                                    </td>
                                    <td className="p-2 text-right tabular-nums">{f.base ? formatMonto(f.base, datos.base.tipo === 'kg' ? 2 : 0) : '—'}</td>
                                    <td className="p-2 text-right tabular-nums">{f.real != null ? `${p}${formatMonto(f.real, 4)}` : '—'}</td>
                                    <td className="p-2 text-right tabular-nums">{f.a_mano != null ? `${p}${formatMonto(f.a_mano, 4)}` : '—'}</td>
                                    <td className="p-2 text-right tabular-nums"><ClaseDif valor={diferencia(f.real, f.a_mano)} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {datos && porcentuales.length > 0 && (
        <section className="bg-white rounded-xl shadow-sm overflow-hidden">
          <h2 className="font-semibold text-slate-700 p-4 border-b">
            Actividades en porcentaje
            <span className="font-normal text-slate-400 text-sm"> · gasto del mes como % de las ventas</span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">Actividad</th>
                  <th className="p-3">Categoría de gasto</th>
                  {datos.meses.map((m) => <th key={m} className="p-3 text-right whitespace-nowrap capitalize">{nombreMes(m)}</th>)}
                  <th className="p-3 text-right whitespace-nowrap">% en estructuras</th>
                </tr>
              </thead>
              <tbody>
                {porcentuales.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="p-3 font-medium">{a.nombre}</td>
                    <td className="p-3">
                      <SelectorCategoria actividad={a} categorias={datos.categorias} puedeEditar={puedeEditar} onCambio={cambiarCategoria} />
                    </td>
                    {a.meses.map((f) => (
                      <td key={f.mes} className="p-3 text-right tabular-nums whitespace-nowrap" title={f.gasto != null ? `Gasto ${p}${formatMonto(f.gasto)} · ventas ${p}${formatMonto(f.ventas)}` : undefined}>
                        {f.real != null ? `${formatMonto(f.real, 1)} %` : <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                    <td className="p-3 text-right whitespace-nowrap">{textoPct(a.pct_recetas)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-3 border-t text-xs text-slate-500">
            En la estructura de costo el % se aplica sobre los insumos de cada producto, no sobre las ventas: esta columna es solo una referencia del peso del gasto.
          </p>
        </section>
      )}

      {datos && datos.actividades.length > 0 && (
        <p className="text-xs text-slate-500 print:hidden">
          El gasto suma los egresos de la categoría y sus subcategorías, sin transferencias entre cuentas propias.
          {datos.base.tipo === 'kg'
            ? ' La base son los kg cargados en Producción → Extrusión.'
            : ' La base son las unidades vendidas: facturas de venta menos notas de crédito y platos cobrados en Caja (sin extras).'}
          {' '}Diferencia en rojo: lo real sale más de 10 % por encima de lo escrito a mano.
        </p>
      )}
    </div>
  );
}

function textoPct(pct) {
  if (!pct) return 'sin usar';
  const rango = pct.minimo === pct.maximo ? `${formatMonto(pct.minimo, 2)} %` : `${formatMonto(pct.minimo, 2)} a ${formatMonto(pct.maximo, 2)} %`;
  return `${rango} · ${pct.productos} ${pct.productos === 1 ? 'producto' : 'productos'}`;
}
